import React, { useEffect, useState } from 'react';
import { CustomService } from '@core/services/custom.service';
import {
  DownloadServiceResponse,
  DownloadServiceSlot,
} from '@core/@models/DownloadServiceModel';
import { finalize } from 'rxjs/operators';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/** File list option with API param value and display name */
export interface FileListOption {
  name: string; // API param value: "newseng"
  display: string; // Show to user: "News(English)"
}

/** Normalize various date string formats to DD/MM/YYYY */
export const normalizeDateStr = (s: string): string => {
  const formats = ['DD/MM/YYYY', 'D/M/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY'];
  for (const fmt of formats) {
    const d = dayjs(s, fmt, true);
    if (d.isValid()) return d.format('DD/MM/YYYY');
  }
  return s;
};

type DateObj = { format: (s: string) => string; date: () => number };

/**
 * Shared hook for download service data.
 * Handles data fetching, file list derivation, available dates,
 * date range state, and date rendering for DatePicker.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useDownloadServiceData() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<DownloadServiceResponse[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedFileList, setSelectedFileList] = useState<string>('');
  const [fileLists, setFileLists] = useState<FileListOption[]>([]);
  const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());

  // Date range state
  const [beginDate, setBeginDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  // Track partial selection (RangePicker fires onChange only after both dates)
  const [partialDate, setPartialDate] = useState<string>('');

  // Fetch data on mount
  useEffect(() => {
    setLoading(true);
    CustomService.getData<DownloadServiceResponse[]>('/download-services/view')
      .pipe(finalize(() => setLoading(false)))
      .subscribe({
        next: (result) => setData(result || []),
        error: () => setData([]),
      });
  }, []);

  // Derive file list options when group changes
  useEffect(() => {
    if (selectedGroup) {
      const group = data.find((d) => d.name === selectedGroup);
      if (group && group.rows.length > 0) {
        const headers = group.headers || [];
        const firstRow = group.rows[0] || [];
        const numColumns = Math.max(headers.length, firstRow.length);
        const fileListOptions: FileListOption[] = [];

        for (let i = 0; i < numColumns; i++) {
          const slot = group.rows
            .map((row) => row[i])
            .find((s): s is DownloadServiceSlot => s != null);
          if (!slot) continue;
          if (slot.name.toLowerCase() === 'date') continue;
          const display =
            headers[i]?.description?.trim() ||
            (slot.files.length > 1 ? slot.name : slot.files[0]?.display) ||
            slot.name;
          fileListOptions.push({ name: slot.name, display });
        }

        setFileLists(fileListOptions);
        setSelectedFileList('');
      }
    } else {
      setFileLists([]);
      setSelectedFileList('');
    }
  }, [selectedGroup, data]);

  // Derive available dates when group or file category changes
  // Filters by selected category and skips slots with no files
  useEffect(() => {
    if (selectedGroup) {
      const group = data.find((d) => d.name === selectedGroup);
      if (group && group.rows.length > 0) {
        const dates = new Set<string>();
        group.rows.forEach((row) => {
          row.forEach((s) => {
            if (!s?.slotDate) return;
            if (selectedFileList && s.name !== selectedFileList) return;
            if (s.files.length === 0) return;
            dates.add(normalizeDateStr(s.slotDate));
          });
        });
        setAvailableDates(dates);
      }
    } else {
      setAvailableDates(new Set());
    }
  }, [selectedGroup, selectedFileList, data]);

  /** Handler for RangePicker onChange */
  const handleDateRangeChange = (dates: unknown) => {
    const range = dates as
      | [{ format: (s: string) => string }, { format: (s: string) => string }]
      | null;
    if (range && range[0] && range[1]) {
      setBeginDate(range[0].format('DD/MM/YYYY'));
      setEndDate(range[1].format('DD/MM/YYYY'));
    } else {
      setBeginDate('');
      setEndDate('');
    }
    setPartialDate('');
  };

  /** Handler for RangePicker onCalendarChange (tracks partial selection) */
  const handleCalendarChange = (dates: unknown) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const range = dates as any;
    if (range && range[0] && !range[1]) {
      setPartialDate(range[0].format('DD/MM/YYYY'));
    } else {
      setPartialDate('');
    }
  };

  /** Check if a date has available files */
  const isDateAvailable = (date: {
    format: (s: string) => string;
  }): boolean => {
    return availableDates.has(date.format('DD/MM/YYYY'));
  };

  /** Check if a date falls within the selected range (including partial) */
  const isDateInRange = (dateStr: string): boolean => {
    if (partialDate && dateStr === partialDate) return true;
    if (beginDate && dateStr === beginDate) return true;
    if (endDate && dateStr === endDate) return true;
    if (!beginDate || !endDate) return false;
    const d = dayjs(dateStr, 'DD/MM/YYYY');
    const start = dayjs(beginDate, 'DD/MM/YYYY');
    const end = dayjs(endDate, 'DD/MM/YYYY');
    return (
      (d.isSame(start) || d.isAfter(start)) &&
      (d.isSame(end) || d.isBefore(end))
    );
  };

  /**
   * Create a dateRender function for DatePicker/RangePicker.
   * Available dates get a light green background unless selected.
   * @param isSelected Optional custom check (defaults to isDateInRange)
   */
  const createDateRender = (isSelected?: (dateStr: string) => boolean) => {
    const checkSelected = isSelected || isDateInRange;
    return function renderDate(current: DateObj) {
      const dateStr = current.format('DD/MM/YYYY');
      const isAvail = isDateAvailable(current);
      const isSel = checkSelected(dateStr);
      if (isAvail && !isSel) {
        return (
          <div
            className="ant-picker-cell-inner"
            style={{
              backgroundColor: 'rgba(82, 196, 26, 0.08)',
              borderRadius: 4,
            }}
          >
            {current.date()}
          </div>
        );
      }
      return <div className="ant-picker-cell-inner">{current.date()}</div>;
    };
  };

  /** Default dateRender using standard range check */
  const dateRender = createDateRender();

  /** Reset date range and partial selection */
  const resetDates = () => {
    setBeginDate('');
    setEndDate('');
    setPartialDate('');
  };

  return {
    data,
    loading,
    selectedGroup,
    setSelectedGroup,
    selectedFileList,
    setSelectedFileList,
    fileLists,
    availableDates,
    beginDate,
    endDate,
    handleDateRangeChange,
    handleCalendarChange,
    isDateInRange,
    dateRender,
    createDateRender,
    resetDates,
  };
}
